import React, { useState, useRef, useEffect } from 'react';
import {
  Check,
} from 'lucide-react';
import { Mold } from '../../types/mold';
import { StatusFilterType } from './types';
import { STATUS_FILTER_CONFIG } from '../../constants/statusFilterConfig';
import { useAppState } from '../../context/AppStateContext';
import { useStatusCounts } from '../../hooks/useStatusCounts';

interface StatusFilterProps {
  molds: Mold[];
  activeStatus?: StatusFilterType;
  onStatusChange: (statuses: StatusFilterType[]) => void;
  className?: string;
  selectedStatuses?: StatusFilterType[];
  isMultiSelect?: boolean;
  availableStatuses?: StatusFilterType[];
}

const StatusFilter: React.FC<StatusFilterProps> = ({
  molds,
  activeStatus,
  onStatusChange,
  className = '',
  selectedStatuses = [],
  isMultiSelect = false,
  availableStatuses = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { mold: { statusLabelMapping } } = useAppState();

  // Use controlled state (selectedStatuses prop) or fallback to activeStatus
  // Ensure we have a flat array of strings
  const currentStatuses = (selectedStatuses.length > 0 
    ? selectedStatuses 
    : (activeStatus ? [activeStatus] : [])
  ).filter((s): s is StatusFilterType => typeof s === 'string');

  // Use the centralized status counts hook
  const statusCounts = useStatusCounts(molds);

  // Get active config based on current selection
  const activeConfig = currentStatuses.length > 0
    ? STATUS_FILTER_CONFIG.find((s) => s.id === currentStatuses[0])
    : STATUS_FILTER_CONFIG.find((s) => s.id === activeStatus);
  
  // Fallback to a default config when no active status
  const displayConfig = activeConfig || {
    color: 'bg-gray-100',
    textColor: 'text-gray-700',
  };
  
  const Icon = activeConfig?.icon;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStatusToggle = (status: StatusFilterType) => {
    if (isMultiSelect) {
      if (status === 'ALL') {
        onStatusChange(['ALL']);
      } else {
        const baseStatuses = currentStatuses.filter((s: StatusFilterType) => s !== 'ALL');
        const newStatuses = baseStatuses.includes(status)
          ? baseStatuses.filter((s: StatusFilterType) => s !== status)
          : [...baseStatuses, status];
        
        onStatusChange(newStatuses.length > 0 ? newStatuses : ['ALL']);
      }
    } else {
      onStatusChange([status]);
      setIsOpen(false);
    }
  };

  const getDisplayLabel = (status: StatusFilterType) => {
    // Safety check - ensure status is a string
    if (!status || typeof status !== 'string') {
      return 'เลือกสถานะ';
    }
    
    // Special cases that don't have mold status mappings
    if (status === 'ALL') {
      return 'สถานะทั้งหมด';
    }
    if (status === 'ATTENTION_REQUIRED') {
      return 'ต้องการการดูแล';
    }
    
    // For mold status filters, use statusLabelMapping from AppState
    // Map StatusFilterType to MoldStatus for lookup
    const statusKey = status.toLowerCase();
    return statusLabelMapping[statusKey] ?? STATUS_FILTER_CONFIG.find(s => s.id === status)?.label ?? status;
  }

  const getDisplayText = () => {
    if (isMultiSelect) {
      if (currentStatuses.length === 0) {
        return 'เลือกสถานะ';
      }
      if (currentStatuses.length === 1) {
        return getDisplayLabel(currentStatuses[0]);
      }
      // Show comma-separated list of selected status labels
      return currentStatuses.map((status: StatusFilterType) => getDisplayLabel(status)).join(', ');
    }
    // For single-select mode, use currentStatuses if available, otherwise activeStatus
    if (currentStatuses.length > 0) {
      return getDisplayLabel(currentStatuses[0]);
    }
    return activeStatus ? getDisplayLabel(activeStatus) : 'เลือกสถานะ';
  }

  return (
    <div className={`relative z-30 ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-full
          ${displayConfig.color}
          border border-transparent
          hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2
          transition-all duration-200
        `}
      >
        {Icon && (
          <Icon className={`w-4 h-4 ${displayConfig.textColor}`} />
        )}
        <span className={`text-sm font-medium ${displayConfig.textColor}`}>
          {getDisplayText()}
        </span>
        <svg
          className={`w-4 h-4 ${displayConfig.textColor} transition-transform ${isOpen ? 'rotate-180' : ''
            }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute mt-2 w-56 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200 py-1">
          {STATUS_FILTER_CONFIG
            .filter((status) => availableStatuses.length === 0 || availableStatuses.includes(status.id))
            .map((status) => {
              const StatusIcon = status.icon;
              const isChecked = isMultiSelect && currentStatuses.includes(status.id);
              return (
                <button
                  key={status.id}
                  onClick={() => handleStatusToggle(status.id)}
                  className={`
                  w-full text-left px-4 py-2 
                  ${status.hoverColor}
                  flex items-center justify-between
                  group
                `}
                >
                  <div className="flex items-center gap-2">
                    {isMultiSelect && (
                      <div
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isChecked
                            ? 'bg-blue-600 border-blue-600'
                            : 'border-gray-300'
                        }`}
                      >
                        {isChecked && <Check className="w-3 h-3 text-white" />}
                      </div>
                    )}
                    <StatusIcon
                      className={`w-4 h-4 ${status.textColor}`}
                    />
                    <span className={`text-sm ${status.textColor}`}>
                      {getDisplayLabel(status?.id)}
                    </span>
                  </div>
                  <span
                    className={`
                    px-1.5 py-0.5
                    ${status.countBg}
                    rounded-full
                    text-xs
                    font-medium
                    ${status.textColor}
                    min-w-[24px]
                    text-center
                  `}
                  >
                    {statusCounts[status.id]}
                  </span>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default StatusFilter;